import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

import Button from '../../../components/ui/Button';

const EquityCurveChart = ({ equityData }) => {
  const [timeRange, setTimeRange] = useState('1M');
  const [isZoomed, setIsZoomed] = useState(false);

  const timeRanges = [
    { label: '7D', value: '7D' },
    { label: '1M', value: '1M' },
    { label: '3M', value: '3M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'ALL', value: 'ALL' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 trading-shadow">
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Balance: <span className="text-primary">${data?.balance?.toLocaleString()}</span>
            </p>
            <p className="text-sm font-medium text-foreground">
              P&L: <span className={data?.pnl >= 0 ? 'text-success' : 'text-error'}>
                {data?.pnl >= 0 ? '+' : ''}${data?.pnl?.toLocaleString()}
              </span>
            </p>
            <p className="text-sm font-medium text-foreground">
              ROI: <span className={data?.roi >= 0 ? 'text-success' : 'text-error'}>
                {data?.roi >= 0 ? '+' : ''}{data?.roi}%
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleExport = (format) => {
    console.log(`Exporting equity curve data as ${format}`);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground mb-2">Equity Curve</h2>
          <p className="text-sm text-muted-foreground">Historical performance and balance progression</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mt-4 lg:mt-0">
          {/* Time Range Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            {timeRanges?.map((range) => (
              <button
                key={range?.value}
                onClick={() => setTimeRange(range?.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md trading-transition ${
                  timeRange === range?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {range?.label}
              </button>
            ))}
          </div>

          {/* Chart Controls */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsZoomed(!isZoomed)}
              iconName={isZoomed ? "ZoomOut" : "ZoomIn"}
              iconPosition="left"
            >
              {isZoomed ? 'Reset' : 'Zoom'}
            </Button>
            
            <div className="relative group">
              <Button variant="ghost" size="sm" iconName="Download">
                Export
              </Button>
              <div className="absolute right-0 top-10 w-32 bg-popover border border-border rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto trading-transition z-50">
                <div className="p-2">
                  <button
                    onClick={() => handleExport('PDF')}
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md trading-transition"
                  >
                    PDF
                  </button>
                  <button
                    onClick={() => handleExport('CSV')}
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md trading-transition"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleExport('Excel')}
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md trading-transition"
                  >
                    Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Chart */}
      <div className="w-full h-80" aria-label="Bot Equity Curve Chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={equityData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#B0B0B0"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#B0B0B0"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${value?.toLocaleString()}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={10000} stroke="#FFD700" strokeDasharray="2 2" />
            <Line
              type="monotone"
              dataKey="balance"
              stroke="#FFD700"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: "#FFD700" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Chart Legend */}
      <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border-secondary">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-0.5 bg-primary"></div>
          <span className="text-sm text-muted-foreground">Account Balance</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-0.5 bg-primary border-dashed border-t-2"></div>
          <span className="text-sm text-muted-foreground">Initial Investment</span>
        </div>
      </div>
    </div>
  );
};

export default EquityCurveChart;